import SiteLayout from "@/components/feature/SiteLayout";
import Seo, { JsonLd } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { Link } from "react-router-dom";

const stats = [
  { value: "07:00", label: "每日首轮快讯上线" },
  { value: "4", label: "内容方向分类" },
  { value: "10 分钟", label: "热榜刷新频率" },
  { value: "1-2 次", label: "每周邮件推送" },
];

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "只整理公开可查的信息",
    desc: "所有内容都基于公开发布的信息来源，并标注时间与出处类型。不存在的信息不写，没确认的事不做确定性表述。",
  },
  {
    icon: "ri-scales-3-line",
    title: "传闻与事实分开呈现",
    desc: "对尚未官方确认的动态，标题或正文中会明确标注「疑似」「网传」，避免让读者把猜测当成结论。",
  },
  {
    icon: "ri-refresh-line",
    title: "有更新就同步更正",
    desc: "当事方做出澄清或官方发布后续消息时，我们会回到原文同步补充，而不是另起一篇制造更多杂乱信息。",
  },
  {
    icon: "ri-focus-3-line",
    title: "少而精，不做标题党",
    desc: "与其堆十条没有营养的快讯，不如把当天最值得关注的几条讲清楚。标题如实反映内容，点击进去不落空。",
  },
];

const testimonials = [
  {
    name: "小满",
    role: "追剧党 · 用了两年",
    text: "以前追一个剧组动态要翻好几个平台，现在早上刷一遍快讯就大概知道发生了什么，省事很多。",
  },
  {
    name: "阿哲",
    role: "综艺爱好者",
    text: "比较喜欢它写传闻会标清楚，不会硬说成真的。看综艺爆料的时候心里有底。",
  },
  {
    name: "Leo",
    role: "内容从业者",
    text: "热榜的排序逻辑挺清楚，而且点进去真的能看到对应内容，不是那种挂了标题就没下文的站。",
  },
];

export default function AboutPage() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 独家吃瓜",
    description:
      "独家吃瓜的品牌介绍、网站定位、内容原则与用户口碑，了解我们如何整理娱乐快讯与今日最新动态。",
    inLanguage: "zh-CN",
    url: "https://wxtmhb.cn/about",
    mainEntity: {
      "@type": "Organization",
      name: "独家吃瓜",
      description: "聚焦娱乐资讯整理的中文内容站，汇总明星八卦、综艺爆料、影视动态与网红热榜。",
      email: "contact@dujiaguacchi.example",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://wxtmhb.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://wxtmhb.cn/about" },
    ],
  };

  return (
    <>
      <Seo
        title="关于我们 - 独家吃瓜品牌介绍与内容原则"
        description="了解独家吃瓜的品牌定位、内容原则与用户口碑。我们汇总娱乐快讯与今日最新动态，坚持传闻与事实分开呈现，有更新就同步更正。"
        keywords="关于独家吃瓜,独家吃瓜介绍,内容原则,娱乐资讯站介绍"
        path="/about"
      />
      <JsonLd data={aboutSchema} />
      <JsonLd data={breadcrumbSchema} />
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "关于我们" }]} />

          <header className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 border border-accent-200 text-accent-800 text-xs font-label mb-4">
              <i className="ri-bookmark-3-line"></i>
              品牌介绍
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight max-w-3xl leading-snug">
              把散落各处的娱乐动态，整理成你早上刷得完的一页
            </h1>
            <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed font-label max-w-3xl">
              「独家吃瓜」是一个聚焦娱乐资讯整理的中文内容站。我们不生产情绪，也不制造反转，只是把每天公开可见的明星动态、综艺路透、影视消息和网红热榜按时间与分类归拢起来，让关注娱乐话题的人不用在几个平台之间反复横跳。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed font-label max-w-3xl">
              网站目前覆盖明星八卦、综艺爆料、影视动态、网红热榜四个方向。首页的快讯滚动播报负责「快」，分类页与文章页负责「全」，两者配合，既能追即时进展，也能回头补上错过的细节。
            </p>
          </header>

          <section className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((item, index) => (
              <div
                key={item.label}
                className={`rounded-lg p-5 border ${
                  index === 1
                    ? "bg-secondary-900 border-secondary-800"
                    : "bg-background-50 border-background-200"
                }`}
              >
                <p
                  className={`font-heading text-2xl md:text-3xl font-bold ${
                    index === 1 ? "text-accent-400" : "text-primary-600"
                  }`}
                >
                  {item.value}
                </p>
                <p
                  className={`mt-2 text-xs font-label ${
                    index === 1 ? "text-background-200" : "text-foreground-600"
                  }`}
                >
                  {item.label}
                </p>
              </div>
            ))}
          </section>

          <section className="mt-12">
            <div className="mb-6">
              <span className="inline-flex items-center gap-1.5 text-xs font-label text-primary-600 mb-2">
                <span className="w-4 h-0.5 bg-primary-500 rounded-full"></span>
                网站定位
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                我们想成为什么样的娱乐资讯站
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="rounded-lg bg-background-100 border border-background-200 p-6">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-xl mb-4">
                  <i className="ri-speed-up-line"></i>
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  快而不乱
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed font-label">
                  热榜和快讯保持高频更新，但每条都尽量交代清楚时间与来源，不用模糊表述制造悬念。
                </p>
              </div>
              <div className="rounded-lg bg-background-100 border border-background-200 p-6">
                <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center text-xl mb-4">
                  <i className="ri-layout-masonry-line"></i>
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  分类清晰
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed font-label">
                  四个方向覆盖娱乐资讯的主要类型，想追哪一类就点哪一类，不必在一堆无关内容里翻找。
                </p>
              </div>
              <div className="rounded-lg bg-background-100 border border-background-200 p-6">
                <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl mb-4">
                  <i className="ri-user-heart-line"></i>
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  对读者负责
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed font-label">
                  我们相信读得越明白，看得越久。因此更新更正、接受反馈，是网站一直在坚持的事。
                </p>
              </div>
            </div>
          </section>

          <section className="mt-12 rounded-xl bg-secondary-950 p-6 md:p-10">
            <div className="mb-6">
              <span className="inline-flex items-center gap-1.5 text-xs font-label text-accent-400 mb-2">
                <span className="w-4 h-0.5 bg-accent-500 rounded-full"></span>
                内容原则
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 tracking-tight">
                四条不轻易妥协的规矩
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg bg-background-100/5 border border-background-100/10 p-5"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-9 h-9 rounded-md bg-accent-500 text-foreground-950 flex items-center justify-center text-lg">
                      <i className={item.icon}></i>
                    </span>
                    <h3 className="font-heading text-base font-semibold text-background-50">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-sm text-background-300 leading-relaxed font-label">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <div className="mb-6">
              <span className="inline-flex items-center gap-1.5 text-xs font-label text-primary-600 mb-2">
                <span className="w-4 h-0.5 bg-primary-500 rounded-full"></span>
                用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                读者怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((item, index) => (
                <blockquote
                  key={item.name}
                  className={`rounded-lg p-6 border ${
                    index === 1
                      ? "bg-accent-50 border-accent-200"
                      : "bg-background-100 border-background-200"
                  }`}
                >
                  <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed font-label">
                    {item.text}
                  </p>
                  <footer className="mt-4 pt-4 border-t border-background-200">
                    <p className="text-sm font-medium text-foreground-900 font-label">{item.name}</p>
                    <p className="text-xs text-foreground-500 font-label mt-0.5">{item.role}</p>
                  </footer>
                </blockquote>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-xl bg-primary-600 p-6 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">
                有想爆料的、想反馈的，都可以直接找我们
              </h2>
              <p className="mt-2 text-sm text-primary-100 font-label">
                联系邮箱：contact@dujiaguacchi.example · 工作日 09:00 - 18:00 回复
              </p>
            </div>
            <Link
              to="/contact"
              className="shrink-0 inline-flex items-center gap-1.5 px-6 py-3 rounded-md bg-background-50 text-primary-700 text-sm font-label hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              前往联系我们
              <i className="ri-arrow-right-line"></i>
            </Link>
          </section>
        </div>
      </SiteLayout>
    </>
  );
}